import { Language, Strings } from "../engine/strings";
import { clearPlayerId } from "../telemetry/telemetry";
import type { UserSettings } from "./userSettings";
import {
	createOverlay,
	createButton,
	createLabeledRadio,
	createLabeledCheckbox,
	createLabeledSlider,
} from "./dom";

export function showSettingsScreen(
	userSettings: UserSettings,
	onClose: () => void,
	onResetHints: () => void,
): void {
	// Level 2 — stacks on top of the pause menu.
	const overlay = createOverlay({
		level: 2,
		onEscape: () => closeOverlay(),
	});
	overlay.panel.classList.add("settings");

	const title = document.createElement("div");
	title.className = "modal-title compact";
	title.textContent = Strings.get("ui_labelSettings") ?? "Settings";
	overlay.panel.appendChild(title);

	// --- Language selector ---
	const langSection = document.createElement("div");
	langSection.className = "form-section";

	const langLabel = document.createElement("div");
	langLabel.className = "form-section-label";
	langLabel.textContent = Strings.get("ui_labelLanguage") ?? "Language:";

	const langOptionsRow = document.createElement("div");
	langOptionsRow.className = "form-row radio-group";

	const english = createLabeledRadio(
		Strings.get("ui_radioButtonEnglish") ?? "English",
		"language",
		"EN",
		userSettings.language === Language.EN,
	);
	const englishOption = document.createElement("div");
	englishOption.className = "radio-option";
	englishOption.appendChild(english.radio);
	englishOption.appendChild(english.label);

	const portuguese = createLabeledRadio(
		Strings.get("ui_radioButtonPortuguese") ?? "Portugues",
		"language",
		"PT_BR",
		userSettings.language === Language.PT_BR,
	);
	const portugueseOption = document.createElement("div");
	portugueseOption.className = "radio-option";
	portugueseOption.appendChild(portuguese.radio);
	portugueseOption.appendChild(portuguese.label);

	langOptionsRow.appendChild(englishOption);
	langOptionsRow.appendChild(portugueseOption);
	langSection.appendChild(langLabel);
	langSection.appendChild(langOptionsRow);
	overlay.panel.appendChild(langSection);

	// --- Sound Effects checkbox ---
	const sound = createLabeledCheckbox(
		Strings.get("ui_labelSoundEffects") ?? "Sound Effects",
		userSettings.soundEnabled,
	);
	const soundRow = document.createElement("div");
	soundRow.className = "checkbox-row";
	soundRow.appendChild(sound.checkbox);
	soundRow.appendChild(sound.label);
	overlay.panel.appendChild(soundRow);

	// --- Sound Volume slider ---
	// Custom two-row layout — build by hand, reuse the slider input.
	const volumeSection = document.createElement("div");
	volumeSection.className = "form-section";

	const volumeLabelRow = document.createElement("div");
	volumeLabelRow.className = "slider-label-row";

	const volumeLabel = document.createElement("span");
	volumeLabel.className = "slider-label";
	volumeLabel.textContent = Strings.get("ui_labelSoundVolume") ?? "Sound Volume";

	const volumeValue = document.createElement("span");
	volumeValue.className = "slider-value";

	const volume = createLabeledSlider(
		Strings.get("ui_labelSoundVolume") ?? "Sound Volume",
		0,
		100,
		1,
		Math.round(userSettings.soundVolume * 100),
	);
	const volumeSlider = volume.slider;

	function updateVolumeDisplay(): void {
		volumeValue.textContent = volumeSlider.value + "%";
	}
	updateVolumeDisplay();

	function updateVolumeEnabled(): void {
		const enabled = sound.checkbox.checked;
		volumeSlider.disabled = !enabled;
		volumeSection.classList.toggle("disabled", !enabled);
	}
	updateVolumeEnabled();

	sound.checkbox.addEventListener("change", updateVolumeEnabled);
	volumeSlider.addEventListener("input", updateVolumeDisplay);

	volumeLabelRow.appendChild(volumeLabel);
	volumeLabelRow.appendChild(volumeValue);
	volumeSection.appendChild(volumeLabelRow);
	volumeSection.appendChild(volumeSlider);
	overlay.panel.appendChild(volumeSection);

	// --- Music checkbox ---
	const music = createLabeledCheckbox(
		Strings.get("ui_labelMusic") ?? "Music",
		userSettings.musicEnabled,
	);
	const musicRow = document.createElement("div");
	musicRow.className = "checkbox-row";
	musicRow.appendChild(music.checkbox);
	musicRow.appendChild(music.label);
	overlay.panel.appendChild(musicRow);

	// --- Music Volume slider ---
	const musicVolumeSection = document.createElement("div");
	musicVolumeSection.className = "form-section";

	const musicVolumeLabelRow = document.createElement("div");
	musicVolumeLabelRow.className = "slider-label-row";

	const musicVolumeLabel = document.createElement("span");
	musicVolumeLabel.className = "slider-label";
	musicVolumeLabel.textContent = Strings.get("ui_labelMusicVolume") ?? "Music Volume";

	const musicVolumeValue = document.createElement("span");
	musicVolumeValue.className = "slider-value";

	const musicVolume = createLabeledSlider(
		Strings.get("ui_labelMusicVolume") ?? "Music Volume",
		0,
		100,
		1,
		Math.round(userSettings.musicVolume * 100),
	);
	const musicVolumeSlider = musicVolume.slider;

	function updateMusicVolumeDisplay(): void {
		musicVolumeValue.textContent = musicVolumeSlider.value + "%";
	}
	updateMusicVolumeDisplay();

	function updateMusicVolumeEnabled(): void {
		const enabled = music.checkbox.checked;
		musicVolumeSlider.disabled = !enabled;
		musicVolumeSection.classList.toggle("disabled", !enabled);
	}
	updateMusicVolumeEnabled();

	music.checkbox.addEventListener("change", updateMusicVolumeEnabled);
	musicVolumeSlider.addEventListener("input", updateMusicVolumeDisplay);

	musicVolumeLabelRow.appendChild(musicVolumeLabel);
	musicVolumeLabelRow.appendChild(musicVolumeValue);
	musicVolumeSection.appendChild(musicVolumeLabelRow);
	musicVolumeSection.appendChild(musicVolumeSlider);
	overlay.panel.appendChild(musicVolumeSection);

	// --- Animation Speed slider ---
	const animSection = document.createElement("div");
	animSection.className = "form-section";

	const animLabelRow = document.createElement("div");
	animLabelRow.className = "slider-label-row";

	const animLabel = document.createElement("span");
	animLabel.className = "slider-label";
	animLabel.textContent = Strings.get("ui_labelAnimationSpeed") ?? "Animation Speed";

	const animValue = document.createElement("span");
	animValue.className = "slider-value";

	const animation = createLabeledSlider(
		Strings.get("ui_labelAnimationSpeed") ?? "Animation Speed",
		1,
		60,
		1,
		Math.round(userSettings.animationSpeedFactor * 10),
	);
	const animSlider = animation.slider;

	function updateAnimDisplay(): void {
		const factor = parseInt(animSlider.value, 10) / 10;
		animValue.textContent = factor.toFixed(1) + "x";
	}
	updateAnimDisplay();

	animSlider.addEventListener("input", updateAnimDisplay);

	animLabelRow.appendChild(animLabel);
	animLabelRow.appendChild(animValue);
	animSection.appendChild(animLabelRow);
	animSection.appendChild(animSlider);
	overlay.panel.appendChild(animSection);

	// --- Camera Follow Player checkbox ---
	const camera = createLabeledCheckbox(
		Strings.get("ui_labelCameraFollowPlayer") ?? "Camera Follow Player",
		userSettings.cameraFollowPlayer,
	);
	const cameraRow = document.createElement("div");
	cameraRow.className = "checkbox-row";
	cameraRow.appendChild(camera.checkbox);
	cameraRow.appendChild(camera.label);
	overlay.panel.appendChild(cameraRow);

	// --- Telemetry checkbox + privacy note ---
	const telemetrySection = document.createElement("div");
	telemetrySection.className = "form-section";

	const telemetry = createLabeledCheckbox(
		Strings.get("ui_labelTelemetry") ?? "Share anonymous gameplay stats",
		!userSettings.telemetryOptOut,
	);
	const telemetryRow = document.createElement("div");
	telemetryRow.className = "checkbox-row";
	telemetryRow.appendChild(telemetry.checkbox);
	telemetryRow.appendChild(telemetry.label);

	const telemetryNote = document.createElement("div");
	telemetryNote.className = "settings-note";
	telemetryNote.textContent =
		Strings.get("ui_telemetryNote") ??
		"Anonymous stats only. No personal data. IP is used briefly for country lookup and abuse protection, never stored.";

	telemetrySection.appendChild(telemetryRow);
	telemetrySection.appendChild(telemetryNote);
	overlay.panel.appendChild(telemetrySection);

	// --- Reset beginner hints button ---
	const resetHintsRow = document.createElement("div");
	resetHintsRow.className = "settings-action-row";

	const resetHintsBtn = createButton(
		Strings.get("ui_btnResetHints") ?? "Show beginner hints again",
		() => {
			onResetHints();
			resetHintsBtn.disabled = true;
			resetHintsBtn.textContent = Strings.get("ui_labelHintsReset") ?? "Hints reset";
		},
		"secondary",
	);
	resetHintsRow.appendChild(resetHintsBtn);
	overlay.panel.appendChild(resetHintsRow);

	// --- Buttons ---
	const buttonRow = document.createElement("div");
	buttonRow.className = "settings-button-row";

	const saveBtn = createButton(
		Strings.get("ui_btnSave") ?? "Save",
		() => {
			const selectedLang = english.radio.checked ? Language.EN : Language.PT_BR;
			userSettings.language = selectedLang;
			Strings.setLanguage(userSettings.language);

			userSettings.soundEnabled = sound.checkbox.checked;
			userSettings.soundVolume = parseInt(volumeSlider.value, 10) / 100;
			userSettings.musicEnabled = music.checkbox.checked;
			userSettings.musicVolume = parseInt(musicVolumeSlider.value, 10) / 100;
			userSettings.animationSpeedFactor = parseInt(animSlider.value, 10) / 10;
			userSettings.cameraFollowPlayer = camera.checkbox.checked;
			userSettings.telemetryOptOut = !telemetry.checkbox.checked;
			if (userSettings.telemetryOptOut) clearPlayerId();
			userSettings.save();
			closeOverlay();
		},
		"primary",
	);

	const cancelBtn = createButton(
		Strings.get("ui_btnCancel") ?? "Cancel",
		() => closeOverlay(),
		"secondary",
	);

	buttonRow.appendChild(saveBtn);
	buttonRow.appendChild(cancelBtn);
	overlay.panel.appendChild(buttonRow);

	function closeOverlay(): void {
		overlay.close();
		onClose();
	}
}
